import React, { Suspense, createContext, lazy, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, Flexbox, TableLoader } from "codekit";

import "./tablerox.css";

import { getRetornoTablero, setRetornoTablero } from "../../libs/redux/slices/trackingSlice/trackingSlice";

/** Widget del catálogo dentro del que corre un tablero (lo lee useDrilldown para el retorno) */
export const WidgetContext = createContext(null);

/** Ancho de la tarjeta según el tamaño del widget (S, M o L) */
export const anchoDe = (tam) => ({ S: "32%", M: "49%", L: "100%" }[tam] ?? "49%");

/**
 * Tarjeta única de widget del catálogo (CIMS0036): cabecera con el nombre
 * (arrastrable si se le pasan las props de arrastre), la ✕ opcional y el
 * widget montado por lazy import desde su ruta del catálogo (GRARUTAX). La
 * usan Mi escritorio y el Inicio, para que un tablero se vea igual en los dos.
 * @author Diego Serrano <diego.serrano@repremundo.com.co>
 * @since 2026-09-14
 * @param {object} widget      Fila del catálogo {GRAIDXXX, GRANOMXX, GRARUTAX, GRATAMXX|TAMEFEXX}
 * @param {function} onQuitar  Si viene, pinta la ✕ (Mi escritorio)
 * @param {object} arrastre    Props del handle de arrastre (dnd-kit), opcional
 * @param {object} refTarjeta  Ref al contenedor exterior, opcional
 * @param {object} estilo      Estilos adicionales del contenedor exterior (transform, transition)
 */
const Tarjewid = ({ widget, onQuitar, arrastre = null, refTarjeta = null, estilo = {} }) => {
  const Componente = useMemo(
    () =>
      lazy(() =>
        import(`../${widget.GRARUTAX}`).catch((error) => {
          console.error("Tarjewid: widget no cargó:", widget.GRARUTAX, error);
          return { default: () => null };
        })
      ),
    [widget.GRARUTAX]
  );

  //Retorno de un drill-down (Diego 2026-09-15): al volver del tracking, la
  //página se desplaza hasta el widget de origen para no perder la visibilidad
  const dispatch = useDispatch();
  const retorno = useSelector(getRetornoTablero);
  const refInterna = useRef(null);
  const bEsOrigen = Boolean(retorno?.widget) && Number(retorno.widget) === Number(widget.GRAIDXXX) && !retorno.enDestino;
  useEffect(() => {
    if (!bEsOrigen) {
      return;
    }
    const nodo = refInterna.current;
    dispatch(setRetornoTablero(null));
    if (nodo) {
      window.setTimeout(() => nodo.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    }
  }, [bEsOrigen, dispatch]);

  return (
    <div ref={(nodo) => { refInterna.current = nodo; if (typeof refTarjeta === "function") refTarjeta(nodo); else if (refTarjeta) refTarjeta.current = nodo; }} id={`widget-${widget.GRAIDXXX}`} style={{ flex: `0 0 ${anchoDe(widget.TAMEFEXX ?? widget.GRATAMXX)}`, minWidth: 280, scrollMarginTop: 96, ...estilo }}>
      <Box rounded="md" border={1} borderColor="neutral-300" bg="white">
        <Box spacing={3} borderBottom={1} borderColor="neutral-200">
          <Flexbox justifyContent="between" alignItems="center">
            <div {...(arrastre ?? {})} style={{ cursor: arrastre ? "grab" : "default", flex: 1 }} title={arrastre ? "Arrastra para reordenar" : undefined}>
              {/* Título del tablero dentro del escritorio: más grande que el texto del tablero para que se lea como
                  encabezado de sección (Diego 2026-09-21) */}
              <span className="tbl-widget-titulo">{widget.GRANOMXX}</span>
            </div>
            {onQuitar && (
              <Button type="button" variant="secondary-light" size="s" icon="close" onlyIcon tooltip="Quitar de mi escritorio" onClick={() => onQuitar(widget)} />
            )}
          </Flexbox>
        </Box>
        <Box spacing={3}>
          <Suspense fallback={<TableLoader />}>
            <WidgetContext.Provider value={widget}>
              <Componente />
            </WidgetContext.Provider>
          </Suspense>
        </Box>
      </Box>
    </div>
  );
};

export default Tarjewid;
